import type { LandingSectionKey } from '../lib/landingTypes'
import { parseLandingSeo, type LandingH2Bindings, type LandingSeo } from '../lib/seoParser'

import rawConfig from './landingPages.config.json'

export type LandingPageConfig = {
  id: string
  routePath: string
  canonicalPath: string
  breadcrumbName: string
  howToTitle?: string
  title?: string
  description?: string
  useCasesImages?: string[]
  benefitsImages?: string[]
  featuresImages?: string[]
  whyChooseImages?: string[]
  demoVideoUrl: string
  defaultInvitationCode: string
  enabledSections?: Partial<Record<LandingSectionKey, boolean>>
  h2Bindings: LandingH2Bindings
  seoDocPath: string
  landingSeo: LandingSeo
  recommendations: Array<{
    icon: string
    name: string
    desc: string
    href: string
  }>
  hreflang?: Record<string, string>
  sitemap?: { changefreq?: string; priority?: number }
}

type LandingPagesRawConfig = typeof rawConfig

const rawDocs = import.meta.glob('../../../docs/**/*.md', { eager: true, query: '?raw', import: 'default' }) as Record<string, string>

function normalizeDocsKey(k: string) {
  return String(k || '')
    .replace(/\\/g, '/')
    .replace(/^(\.\.\/){3}docs\//, 'docs/')
    .replace(/^\/*/, '')
}

const seoRawByDoc: Record<string, string> = Object.fromEntries(
  Object.entries(rawDocs).map(([k, v]) => [normalizeDocsKey(k), String(v || '')]),
)

const rawPages = (rawConfig as LandingPagesRawConfig).pages

export const landingPages: LandingPageConfig[] = rawPages.map((p: any) => {
  const seoRaw = seoRawByDoc[normalizeDocsKey(String(p.seoDocPath || ''))] || ''
  const bindings = (p.h2Bindings || {}) as LandingH2Bindings
  const howToTitle = Object.entries(bindings).find(([, v]) => v === 'howTo')?.[0] || ''
  return {
    id: String(p.id),
    routePath: String(p.routePath),
    canonicalPath: String(p.canonicalPath),
    breadcrumbName: String(p.breadcrumbName),
    howToTitle: howToTitle ? String(howToTitle) : undefined,
    title: String(p.title || '').trim() || undefined,
    description: String(p.description || '').trim() || undefined,
    useCasesImages: Array.isArray(p.useCasesImages) ? p.useCasesImages : undefined,
    benefitsImages: Array.isArray(p.benefitsImages) ? p.benefitsImages : undefined,
    featuresImages: Array.isArray(p.featuresImages) ? p.featuresImages : undefined,
    whyChooseImages: Array.isArray(p.whyChooseImages) ? p.whyChooseImages : undefined,
    demoVideoUrl: String(p.demoVideoUrl),
    defaultInvitationCode: String(p.defaultInvitationCode),
    enabledSections: (p.enabledSections && Object.keys(p.enabledSections).length ? p.enabledSections : undefined) as any,
    h2Bindings: bindings,
    seoDocPath: String(p.seoDocPath),
    landingSeo: parseLandingSeo(seoRaw, bindings),
    recommendations: Array.isArray(p.recommendations) ? p.recommendations : [],
    hreflang: (p.hreflang || undefined) as any,
    sitemap: (p.sitemap || undefined) as any,
  }
})

export const defaultLandingPage = landingPages[0] as LandingPageConfig
